import { reactive } from 'vue'
import { AssetSource } from '@ve-pro/enums/asset'
import type { SourceIdArrayType } from './index.type'
import type { TableDataConfig } from '@pt/api/assets/index.type'
import type { AssetGroupInfo } from '@ve-pro/api/asset-group/type'
import type { AsssetsModel, ProjectAssetsModel } from '@ve-pro/api/aside/type'

// 把项目素材转成资产库资源格式，以兼容资产库默认选中状态
export function transformAssetTypeData(
  project: ProjectAssetsModel,
): TableDataConfig {
  return {
    id: String(project.source_id),
    width: project.width,
    height: project.height,
    name: project.name,
    url: '',
    full_url: project.file_url,
    ratio: 0,
    fps: 0,
    file_size: project.file_size,
    format: project.file_ext,
    payload: '',
    fingerprint: project.file_md5,
    description: '',
    duration: project.duration,
    data_source: 0,
    creative_type: project.type,
    updated_time: project.updated_time,
    created_time: project.created_time,
    bit_rate: 0,
    cover_url: project.thumb_url,
    status: project.status,
    progress: project.progress,
  }
}

// 资产库来的数据，有source字段 AssetSource.Asset
// 用户资源来的数据，有source字段 AssetSource.User
// 公共资源来的数据，没有source字段 AssetSource.Public
export type Assets =
  | (AsssetsModel & {
      source?: AssetSource
      source_id?: ProjectAssetsModel['source_id']
    })
  | ProjectAssetsModel

export const multipleState = reactive<{
  data: Assets[]
}>({
  data: [],
})

/**
 * 选中资源
 * @param asset
 * @param convert 是否需要转换,是否以source_id比对添加
 */
export function selectAsset(asset: Assets, convert = true): void {
  // 相同的资源只能添加一次
  if (convert) {
    if (!multipleState.data.some((item) => item.source_id === asset.id)) {
      multipleState.data.push(asset)
    }
  } else if (!multipleState.data.some((item) => item.id === asset.id)) {
    multipleState.data.push(asset)
  }
}

/**
 * 删除资源
 * @param asset
 * @param convert 是否以source_id比对删除
 */
export function removeAsset(asset: Assets, convert = false): void {
  if (convert) {
    const index = multipleState.data.findIndex(
      (item) => item.source_id === asset.source_id,
    )
    if (index > -1) {
      multipleState.data.splice(index, 1)
    }
  } else {
    const index = multipleState.data.findIndex((item) => item.id === asset.id)
    if (index > -1) {
      multipleState.data.splice(index, 1)
    }
  }
}

// 清空选中数据
export function cancelSelectMode(): void {
  multipleState.data = []
}

// 批量添加资源弹窗数据禁用合集
export function disabledDataCollection(
  list: AssetGroupInfo['assets'],
): SourceIdArrayType {
  const sourceIdArray = reactive<SourceIdArrayType>({
    disableAssetIds: [],
    disableCommonIds: [],
    disableProjectIds: [],
  })

  if (list?.length) {
    // 项目素材禁用id合集(1: 历史数据 3:资产库同步到项目素材的资源 6: 从模版复制过来的资源)
    sourceIdArray.disableProjectIds = Array.from(
      list.filter(
        (item) =>
          item.source === AssetSource.User ||
          item.source === AssetSource.Asset ||
          item.source === AssetSource.Template,
      ),
      ({ source_id }) => String(source_id),
    )
    // 资产库禁用id合集(3:资产库资源 6: 从模版复制过来的资源)
    sourceIdArray.disableAssetIds = Array.from(
      list.filter(
        (item) =>
          item.source === AssetSource.Asset ||
          item.source === AssetSource.Template,
      ),
      ({ source_id }) => String(source_id),
    )
    // 公共库禁用id合集
    sourceIdArray.disableCommonIds = Array.from(
      list.filter((item) => item.source === AssetSource.Public),
      ({ source_id }) => Number(source_id),
    )
  }

  return sourceIdArray
}
